import * as types from '@/types/cutting';
import {
  mockCuttingOrders,
  mockCutConfigs,
  mockCutConfigLines,
  mockCuttingOrderLines
} from '@/api/mockData/cutting';
import { api } from '@/api/axios';

// Simulate API delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Helper function to filter and paginate data
const filterAndPaginate = <T>(
  data: T[],
  page: number = 1,
  pageSize: number = 10,
  filterFn?: (item: T) => boolean
) => {
  const filteredData = filterFn ? data.filter(filterFn) : data;
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  return {
    data: filteredData.slice(startIndex, endIndex),
    total: filteredData.length,
    page,
    pageSize,
  };
};

// Generate cutting order code
const generateCuttingOrderCode = (): string => {
  const nextNumber = mockCuttingOrders.length + 1;
  return `LC${String(nextNumber).padStart(3, '0')}`;
};

// Generate cut config ID
const generateCutConfigId = (): string => {
  const nextNumber = mockCutConfigs.length + 1;
  return `CFG${String(nextNumber).padStart(3, '0')}`;
};

export const cuttingApi = {
  // ===== BACKEND-ALIGNED CUTTING FORMS API =====

  // Get cutting forms list with filters and pagination
  getCuttingForms: async (
    page: number = 1,
    pageSize: number = 10,
    filters?: types.CuttingFormFilters
  ): Promise<types.CuttingFormListResponse> => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });

      if (filters?.search) {
        queryParams.append('search', filters.search);
      }
      if (filters?.status) {
        queryParams.append('status', filters.status);
      }
      if (filters?.sortBy) {
        queryParams.append('sort_by', filters.sortBy);
      }
      if (filters?.sort_order) {
        queryParams.append('sort_order', filters.sort_order);
      }

      const response = await api.get(`/cutting-forms?${queryParams}`);

      // Extract data from StandardResponse wrapper
      return {
        data: response.data.data || [],
        total: response.data.total || 0,
        page: response.data.page || page,
        pageSize: response.data.pageSize || pageSize,
      };
    } catch (error: any) {
      console.error('Error fetching cutting forms:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch cutting forms'
      );
    }
  },

  // Get single cutting form by code
  getCuttingForm: async (code: string): Promise<types.CuttingFormResponse> => {
    try {
      const response = await api.get(`/cutting-forms/${code}`);
      return response.data.data;
    } catch (error: any) {
      console.error(`Error fetching cutting form ${code}:`, error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch cutting form'
      );
    }
  },

  // Create new cutting form
  createCuttingForm: async (data: types.CreateCuttingFormRequest): Promise<types.CuttingFormResponse> => {
    try {
      const response = await api.post('/cutting-forms', data);
      return response.data.data;
    } catch (error: any) {
      console.error('Error creating cutting form:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to create cutting form'
      );
    }
  },

  // Update cutting form
  updateCuttingForm: async (code: string, data: types.UpdateCuttingFormRequest): Promise<types.CuttingFormResponse> => {
    try {
      const response = await api.put(`/cutting-forms/${code}`, data);
      return response.data.data;
    } catch (error: any) {
      console.error(`Error updating cutting form ${code}:`, error);
      throw new Error(
        error.response?.data?.message || 'Failed to update cutting form'
      );
    }
  },

  // Delete cutting form
  deleteCuttingForm: async (code: string): Promise<void> => {
    try {
      await api.delete(`/cutting-forms/${code}`);
    } catch (error: any) {
      console.error(`Error deleting cutting form ${code}:`, error);
      throw new Error(
        error.response?.data?.message || 'Failed to delete cutting form'
      );
    }
  },

  // Update cutting form status (generic)
  updateCuttingFormStatus: async (code: string, status: types.CuttingFormStatus): Promise<types.CuttingFormResponse> => {
    try {
      const response = await api.patch(`/cutting-forms/${code}/string-status`, { status });
      return response.data.data;
    } catch (error: any) {
      console.error(`Error updating cutting form ${code} status to ${status}:`, error);
      throw new Error(
        error.response?.data?.message || 'Failed to update cutting form status'
      );
    }
  },

  // Specific status transition methods
  cutCuttingForm: async (code: string): Promise<types.CuttingFormResponse> => {
    try {
      const response = await api.patch(`/cutting-forms/${code}/status/cut`);
      return response.data.data;
    } catch (error: any) {
      console.error(`Error cutting cutting form ${code}:`, error);
      throw new Error(
        error.response?.data?.message || 'Failed to cut cutting form'
      );
    }
  },

  confirmCuttingForm: async (code: string): Promise<types.CuttingFormResponse> => {
    try {
      const response = await api.patch(`/cutting-forms/${code}/status/confirm`);
      return response.data.data;
    } catch (error: any) {
      console.error(`Error confirming cutting form ${code}:`, error);
      throw new Error(
        error.response?.data?.message || 'Failed to confirm cutting form'
      );
    }
  },

  recheckCuttingForm: async (code: string): Promise<types.CuttingFormResponse> => {
    try {
      const response = await api.patch(`/cutting-forms/${code}/status/recheck`);
      return response.data.data;
    } catch (error: any) {
      console.error(`Error rechecking cutting form ${code}:`, error);
      throw new Error(
        error.response?.data?.message || 'Failed to recheck cutting form'
      );
    }
  },

  // Update all actual quantities for a cutting form and its lines
  updateAllActualQuantities: async (formCode: string, payload: types.UpdateCuttingFormQuantitiesRequest): Promise<void> => {
    try {
      await api.put(`/cutting-forms/${formCode}/outputs`, payload);
    } catch (error: any) {
      console.error(`Error updating all actual quantities for cutting form ${formCode}:`, error);
      throw new Error(
        error.response?.data?.message || 'Failed to update all actual quantities'
      );
    }
  },

  // Update actual quantities for cutting form outputs
  updateActualQuantities: async (code: string, data: any): Promise<void> => {
    try {
      const response = await api.put(`/cutting-forms/${code}/outputs`, data);
      return response.data;
    } catch (error: any) {
      console.error(`Error updating actual quantities for cutting form ${code}:`, error);
      throw new Error(
        error.response?.data?.message || 'Failed to update actual quantities'
      );
    }
  },

  // Get cutting form detail (form + lines)
  getCuttingFormDetail: async (code: string): Promise<{ form: types.CuttingFormResponse; lines: types.CuttingFormLine[] }> => {
    try {
      // Step 1: Get the main form details using code
      const formResponse = await api.get(`/cutting-forms/${code}`);
      const form = formResponse.data.data;

      if (!form || !form.code) {
        throw new Error('Cutting form code not found');
      }

      // Step 2: Use the code to fetch the lines
      const linesResponse = await api.get(`/cutting-form-lines?cutting_form_id=${form.code}&page_size=1000`);

      return {
        form: form,
        lines: linesResponse.data.data || [],
      };
    } catch (error: any) {
      console.error(`Error fetching cutting form detail for ${code}:`, error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch cutting form detail'
      );
    }
  },

  // ===== CUTTING FORM LINES API =====

  // Get cutting form lines by cutting form code
  getCuttingFormLines: async (cuttingFormCode: string): Promise<any[]> => {
    try {
      const params = new URLSearchParams({
        page: '1',
        page_size: '100',
        cutting_form_id: cuttingFormCode,
      });

      const response = await api.get(`/cutting-form-lines?${params.toString()}`);
      return response.data.data || [];
    } catch (error: any) {
      console.error(`Error fetching cutting form lines for ${cuttingFormCode}:`, error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch cutting form lines'
      );
    }
  },

  // Create multiple cutting form lines at once
  createManyCuttingFormLines: async (cuttingFormLines: any[]): Promise<any[]> => {
    try {
      const response = await api.post('/cutting-form-lines/bulk', cuttingFormLines);
      return response.data.data;
    } catch (error: any) {
      console.error('Error creating cutting form lines:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to create cutting form lines'
      );
    }
  },

  // Create a single cutting form line
  createCuttingFormLine: async (cuttingFormLine: any): Promise<any> => {
    try {
      console.log('🚀 API: createCuttingFormLine called with data:', cuttingFormLine);
      console.log('🚀 API: Making POST request to /cutting-form-lines');
      const response = await api.post('/cutting-form-lines', cuttingFormLine);
      console.log('🚀 API: createCuttingFormLine response:', response.data);
      return response.data.data;
    } catch (error: any) {
      console.error('❌ API: Error creating cutting form line:', error);
      console.error('❌ API: Error response:', error.response?.data);
      throw new Error(
        error.response?.data?.message || 'Failed to create cutting form line'
      );
    }
  },

  // Update a cutting form line
  updateCuttingFormLine: async (code: string, cuttingFormLine: any): Promise<any> => {
    try {
      console.log('🚀 API: updateCuttingFormLine called with code:', code, 'data:', cuttingFormLine);
      console.log(`🚀 API: Making PUT request to /cutting-form-lines/${code}`);
      const response = await api.put(`/cutting-form-lines/${code}`, cuttingFormLine);
      console.log('🚀 API: updateCuttingFormLine response:', response.data);
      return response.data.data;
    } catch (error: any) {
      console.error('❌ API: Error updating cutting form line:', error);
      console.error('❌ API: Error response:', error.response?.data);
      throw new Error(
        error.response?.data?.message || 'Failed to update cutting form line'
      );
    }
  },

  // Delete a cutting form line
  deleteCuttingFormLine: async (code: string): Promise<void> => {
    try {
      console.log('🚀 API: deleteCuttingFormLine called with code:', code);
      console.log(`🚀 API: Making DELETE request to /cutting-form-lines/${code}`);
      await api.delete(`/cutting-form-lines/${code}`);
      console.log('🚀 API: deleteCuttingFormLine completed successfully');
    } catch (error: any) {
      console.error('❌ API: Error deleting cutting form line:', error);
      console.error('❌ API: Error response:', error.response?.data);
      throw new Error(
        error.response?.data?.message || 'Failed to delete cutting form line'
      );
    }
  },

  // ===== LEGACY CUTTING ORDERS API (for backward compatibility) =====

  // Get cutting orders list with filters and pagination
  getCuttingOrders: async (
    page: number = 1,
    pageSize: number = 10,
    filters?: types.CuttingOrderFilters
  ): Promise<types.CuttingOrderListResponse> => {
    await delay(500);

    const filterFn = (order: types.CuttingOrder) => {
      if (filters?.status && filters.status.length > 0 && !filters.status.includes(order.status)) {
        return false;
      }
      if (filters?.search) {
        const searchLower = filters.search.toLowerCase();
        return !!(
          order.code.toLowerCase().includes(searchLower) ||
          order.createdBy.toLowerCase().includes(searchLower) ||
          (order.notes && order.notes.toLowerCase().includes(searchLower))
        );
      }
      if (filters?.createdBy && order.createdBy !== filters.createdBy) {
        return false;
      }
      if (filters?.dateFrom && order.date < filters.dateFrom) {
        return false;
      }
      if (filters?.dateTo && order.date > filters.dateTo) {
        return false;
      }
      if (filters?.cutType && filters.cutType.length > 0) {
        const orderCutTypes = order.cutLines.map(line => line.cutType);
        return filters.cutType.some(type => orderCutTypes.includes(type));
      }
      return true;
    };

    return filterAndPaginate(mockCuttingOrders, page, pageSize, filterFn);
  },

  // Get single cutting order by ID
  getCuttingOrder: async (id: string): Promise<types.CuttingOrder> => {
    await delay(300);

    const order = mockCuttingOrders.find(o => o.id === id);
    if (!order) {
      throw new Error(`Cutting order with ID ${id} not found`);
    }
    return order;
  },

  // Get cutting order detail with related configs
  getCuttingOrderDetail: async (id: string): Promise<types.CuttingOrderDetailResponse> => {
    await delay(500);

    const order = mockCuttingOrders.find(o => o.id === id);
    if (!order) {
      throw new Error(`Cutting order with ID ${id} not found`);
    }

    const configIds = [...new Set(order.cutLines.map(line => line.cutConfigId).filter(Boolean))];
    const cutConfigs = mockCutConfigs.filter(config => configIds.includes(config.id));

    return {
      cuttingOrder: order,
      cutConfigs,
    };
  },

  // Create new cutting order
  createCuttingOrder: async (data: types.CreateCuttingOrderRequest): Promise<types.CuttingOrder> => {
    await delay(800);

    const newOrder: types.CuttingOrder = {
      id: `CO${String(mockCuttingOrders.length + 1).padStart(3, '0')}`,
      code: generateCuttingOrderCode(),
      date: data.date,
      createdBy: 'Current User', // In real app, get from auth context
      notes: data.notes,
      status: 'created',
      cutLines: data.cutLines.map((line, index) => ({
        id: `COL${String(mockCuttingOrderLines.length + index + 1).padStart(3, '0')}`,
        cuttingOrderId: `CO${String(mockCuttingOrders.length + 1).padStart(3, '0')}`,
        stt: index + 1,
        materialName: line.materialName,
        cutType: line.cutType,
        quantity: line.quantity,
        unit: line.unit,
        text: line.text,
        cutConfigId: line.cutConfigId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })) as types.CuttingOrderLine[],
      totalQuantity: data.cutLines.reduce((sum, line) => sum + line.quantity, 0),
      totalConfigs: [...new Set(data.cutLines.map(line => line.cutConfigId).filter(Boolean))].length,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    mockCuttingOrders.push(newOrder);

    // Update usage count for used configs
    data.cutLines.forEach(line => {
      if (line.cutConfigId) {
        const config = mockCutConfigs.find(c => c.id === line.cutConfigId);
        if (config) {
          config.usageCount++;
          config.updatedAt = new Date().toISOString();
        }
      }
    });

    return newOrder;
  },

  // Update cutting order
  updateCuttingOrder: async (data: types.UpdateCuttingOrderRequest): Promise<types.CuttingOrder> => {
    await delay(600);

    const index = mockCuttingOrders.findIndex(o => o.id === data.id);
    if (index === -1) {
      throw new Error(`Cutting order with ID ${data.id} not found`);
    }

    const updatedOrder = {
      ...mockCuttingOrders[index],
      ...data,
      updatedAt: new Date().toISOString(),
    } as types.CuttingOrder;

    mockCuttingOrders[index] = updatedOrder;
    return updatedOrder;
  },

  // Delete cutting order
  deleteCuttingOrder: async (id: string): Promise<void> => {
    await delay(400);

    const index = mockCuttingOrders.findIndex(o => o.id === id);
    if (index === -1) {
      throw new Error(`Cutting order with ID ${id} not found`);
    }

    mockCuttingOrders.splice(index, 1);
  },

  // ===== CUT CONFIGS =====

  // Get cut configs list with filters and pagination
  getCutConfigs: async (
    page: number = 1,
    pageSize: number = 10,
    filters?: types.CutConfigFilters
  ): Promise<types.CutConfigListResponse> => {
    await delay(400);

    const filterFn = (config: types.CutConfig) => {
      if (filters?.cutType && config.cutType !== filters.cutType) {
        return false;
      }
      if (filters?.search) {
        const searchLower = filters.search.toLowerCase();
        return (
          config.name.toLowerCase().includes(searchLower) ||
          config.materialType.toLowerCase().includes(searchLower) ||
          (config.description && config.description.toLowerCase().includes(searchLower))
        );
      }
      if (filters?.materialType && config.materialType !== filters.materialType) {
        return false;
      }
      if (filters?.createdBy && config.createdBy !== filters.createdBy) {
        return false;
      }
      return true;
    };

    let filteredConfigs = mockCutConfigs.filter(filterFn);

    // Apply sorting
    if (filters?.sortBy) {
      filteredConfigs.sort((a, b) => {
        let aValue: any, bValue: any;

        switch (filters.sortBy) {
          case 'name':
            aValue = a.name;
            bValue = b.name;
            break;
          case 'usageCount':
            aValue = a.usageCount;
            bValue = b.usageCount;
            break;
          case 'createdAt':
            aValue = new Date(a.createdAt);
            bValue = new Date(b.createdAt);
            break;
          default:
            return 0;
        }

        if (filters.sortOrder === 'desc') {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        } else {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        }
      });
    }

    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return {
      data: filteredConfigs.slice(startIndex, endIndex),
      total: filteredConfigs.length,
      page,
      pageSize,
    };
  },

  // Get single cut config by ID
  getCutConfig: async (id: string): Promise<types.CutConfig> => {
    await delay(300);

    const config = mockCutConfigs.find(c => c.id === id);
    if (!config) {
      throw new Error(`Cut config with ID ${id} not found`);
    }
    return config;
  },

  // Create new cut config
  createCutConfig: async (data: types.CreateCutConfigRequest): Promise<types.CutConfig> => {
    await delay(800);

    const newConfigId = generateCutConfigId();

    const newConfig: types.CutConfig = {
      id: newConfigId,
      name: data.name,
      cutType: data.cutType,
      materialType: data.materialType,
      description: data.description,
      createdBy: 'Current User',
      usageCount: 0,
      cutConfigLines: data.cutConfigLines.map((line, index) => ({
        id: `CL${String(mockCutConfigLines.length + index + 1).padStart(3, '0')}`,
        cutConfigId: newConfigId,
        finalSize: line.finalSize,
        quantity: line.quantity,
        unit: line.unit,
        notes: line.notes,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })) as types.CutConfigLine[],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    mockCutConfigs.push(newConfig);
    return newConfig;
  },

  // Update cut config
  updateCutConfig: async (data: types.UpdateCutConfigRequest): Promise<types.CutConfig> => {
    await delay(600);

    const index = mockCutConfigs.findIndex(c => c.id === data.id);
    if (index === -1) {
      throw new Error(`Cut config with ID ${data.id} not found`);
    }

    const updatedConfig = {
      ...mockCutConfigs[index],
      ...data,
      updatedAt: new Date().toISOString(),
    } as types.CutConfig;

    mockCutConfigs[index] = updatedConfig;
    return updatedConfig;
  },

  // Delete cut config
  deleteCutConfig: async (id: string): Promise<void> => {
    await delay(400);

    const index = mockCutConfigs.findIndex(c => c.id === id);
    if (index === -1) {
      throw new Error(`Cut config with ID ${id} not found`);
    }

    // Check if config is being used
    const isUsed = mockCuttingOrderLines.some(line => line.cutConfigId === id);
    if (isUsed) {
      throw new Error('Cannot delete cut config that is being used in cutting orders');
    }

    mockCutConfigs.splice(index, 1);
  },

  // Get cutting order statistics
  getCuttingOrderStats: async (): Promise<types.CuttingOrderStatsResponse> => {
    await delay(300);

    const mostUsedConfigs = mockCutConfigs
      .sort((a, b) => b.usageCount - a.usageCount)
      .slice(0, 5);

    return {
      totalOrders: mockCuttingOrders.length,
      createdOrders: mockCuttingOrders.filter(o => o.status === 'created').length,
      cutOrders: mockCuttingOrders.filter(o => o.status === 'cut').length,
      confirmedOrders: mockCuttingOrders.filter(o => o.status === 'confirmed').length,
      totalConfigs: mockCutConfigs.length,
      mostUsedConfigs,
    };
  },

  // Export cutting order data
  exportCuttingOrderData: async (options: types.ExportCuttingOrderOptions): Promise<Blob> => {
    await delay(1000);

    const mockData = JSON.stringify({
      options,
      data: mockCuttingOrders,
      exportedAt: new Date().toISOString(),
    });

    return new Blob([mockData], { type: 'application/json' });
  },

  // Search cut configs by size (for template selection)
  searchCutConfigsBySize: async (
    cutType: types.CutType,
    searchQuery: string,
    limit: number = 10
  ): Promise<types.CutConfig[]> => {
    await delay(300);

    const searchLower = searchQuery.toLowerCase();

    return mockCutConfigs
      .filter(config =>
        config.cutType === cutType &&
        (config.name.toLowerCase().includes(searchLower) ||
         config.materialType.toLowerCase().includes(searchLower))
      )
      .sort((a, b) => b.usageCount - a.usageCount) // Sort by usage frequency
      .slice(0, limit);
  },
};
